import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast from './Toast';

// Create the context
const ToastContext = createContext();

// Maximum number of toasts to show simultaneously
const MAX_TOASTS = 5;

// Toast Provider component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Remove a specific toast by ID
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Show a success toast with the given message (default: 3 seconds)
  const showSuccess = useCallback((message, duration = 3000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type: 'success', duration };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      // Keep only the last MAX_TOASTS toasts
      return updated.slice(-MAX_TOASTS);
    });

    // Auto-dismiss after duration
    setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  // Show an error toast with the given message (default: 5 seconds)
  const showError = useCallback((message, duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type: 'error', duration };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      return updated.slice(-MAX_TOASTS);
    });

    setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  // Show a warning toast (default: 4 seconds)
  const showWarning = useCallback((message, duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type: 'warning', duration };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      return updated.slice(-MAX_TOASTS);
    });

    setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ showSuccess, showError, showWarning }}>
      {children}
      <div className="toast-container">
        {toasts.map((toast, index) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
            duration={toast.duration}
            index={index}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Custom hook for using toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
